import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  ScrollText,
  AlertTriangle,
  Trophy,
  FileText,
  Users,
  Newspaper,
  Network,
  Layers,
  BookOpen as BookOpenIcon,
  Settings as SettingsIcon,
  Phone,
  Upload,
  Paperclip,
} from "lucide-react";
import api, { formatApiError, formatDate } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Seo from "../components/Seo";

const TABS = [
  { key: "announcements", label: "Úradné správy", icon: ScrollText, roles: ["admin", "referee"] },
  { key: "notices", label: "Dôležité oznamy", icon: AlertTriangle, roles: ["admin"] },
  { key: "competitions", label: "Súťaže", icon: Trophy, roles: ["admin"] },
  { key: "documents", label: "Dokumenty", icon: FileText, roles: ["admin"] },
  { key: "people", label: "Rozhodcovia / Delegáti", icon: Users, roles: ["admin", "referee"] },
  { key: "news", label: "Aktuality", icon: Newspaper, roles: ["admin"] },
  { key: "projects", label: "Projekty", icon: Layers, roles: ["admin"] },
  { key: "structure", label: "Štruktúra", icon: Network, roles: ["admin"] },
  { key: "info_pages", label: "Info stránky", icon: BookOpenIcon, roles: ["admin"] },
  { key: "contact", label: "Kontakt", icon: Phone, roles: ["admin"] },
  { key: "settings", label: "Nastavenia", icon: SettingsIcon, roles: ["admin"] },
];

const today = () => new Date().toISOString().slice(0, 10);

const SCHEMAS = {
  announcements: {
    endpoint: "/announcements",
    fields: [
      { name: "category", label: "Kategória", type: "select", options: ["ŠTK", "DK", "KR", "Obsadenie R", "Výkonný výbor", "Odvolacia komisia", "Sekretár", "TMK", "KM", "MaK", "Rada"], required: true },
      { name: "number", label: "Číslo (napr. č. 32)", type: "text" },
      { name: "date", label: "Dátum", type: "date", required: true },
      { name: "title", label: "Názov", type: "text", required: true },
      { name: "excerpt", label: "Krátky popis (úryvok)", type: "textarea" },
      { name: "content", label: "Obsah správy", type: "longtext", required: true },
      { name: "pinned", label: "Pripnúť (zobraziť na vrchu)", type: "checkbox" },
      { name: "attachment_url", label: "Príloha (PDF, obrázok, dokument)", type: "attachment" },
    ],
    columns: [
      { key: "date", label: "Dátum", render: (v) => formatDate(v) },
      { key: "category", label: "Kat." },
      { key: "title", label: "Názov" },
    ],
    defaults: () => ({ category: "ŠTK", number: "", date: today(), title: "", excerpt: "", content: "", pinned: false, attachment_url: "", attachment_name: "" }),
  },
  notices: {
    endpoint: "/notices",
    fields: [
      { name: "title", label: "Názov", type: "text", required: true },
      { name: "excerpt", label: "Krátky popis", type: "textarea", required: true },
      { name: "date", label: "Dátum", type: "date", required: true },
      { name: "link", label: "Odkaz (interný /uradne-spravy alebo URL)", type: "text" },
      { name: "type", label: "Typ", type: "select", options: ["default", "important", "warning"] },
      { name: "attachment_url", label: "Príloha (PDF, obrázok, dokument)", type: "attachment" },
    ],
    columns: [
      { key: "date", label: "Dátum", render: (v) => formatDate(v) },
      { key: "title", label: "Názov" },
      { key: "type", label: "Typ" },
    ],
    defaults: () => ({ title: "", excerpt: "", date: today(), link: "", type: "default", attachment_url: "", attachment_name: "" }),
  },
  competitions: {
    endpoint: "/competitions",
    fields: [
      { name: "name", label: "Názov súťaže", type: "text", required: true },
      { name: "category", label: "Kategória", type: "select", options: ["seniors", "youth", "cup"], required: true },
      { name: "description", label: "Popis", type: "textarea" },
      { name: "results_url", label: "URL — Výsledky", type: "text" },
      { name: "table_url", label: "URL — Tabuľka", type: "text" },
      { name: "schedule_url", label: "URL — Rozpis", type: "text" },
      { name: "documents_url", label: "URL — Dokumenty", type: "text" },
      { name: "order", label: "Poradie", type: "number" },
    ],
    columns: [
      { key: "name", label: "Názov" },
      { key: "category", label: "Kategória" },
      { key: "order", label: "Poradie" },
    ],
    defaults: () => ({ name: "", category: "seniors", description: "", results_url: "", table_url: "", schedule_url: "", documents_url: "", order: 0 }),
  },
  documents: {
    endpoint: "/documents",
    fields: [
      { name: "name", label: "Názov dokumentu", type: "text", required: true },
      { name: "category", label: "Kategória", type: "select", options: ["rozpis", "pravidla", "registracne", "tlaciva", "smernice"], required: true },
      { name: "file_url", label: "URL súboru", type: "text", required: true },
      { name: "date", label: "Dátum", type: "date", required: true },
      { name: "size", label: "Veľkosť (napr. 120 kB)", type: "text" },
    ],
    columns: [
      { key: "date", label: "Dátum", render: (v) => formatDate(v) },
      { key: "category", label: "Kat." },
      { key: "name", label: "Názov" },
    ],
    defaults: () => ({ name: "", category: "smernice", file_url: "", date: today(), size: "" }),
  },
  people: {
    endpoint: "/people",
    fields: [
      { name: "name", label: "Meno", type: "text", required: true },
      { name: "role", label: "Funkcia", type: "text" },
      { name: "type", label: "Typ", type: "select", options: ["referee", "delegate", "committee"], required: true },
      { name: "location", label: "Mesto / Klub", type: "text" },
      { name: "phone", label: "Telefón", type: "text" },
      { name: "email", label: "E-mail", type: "text" },
      { name: "order", label: "Poradie", type: "number" },
    ],
    columns: [
      { key: "name", label: "Meno" },
      { key: "type", label: "Typ" },
      { key: "role", label: "Funkcia" },
    ],
    defaults: () => ({ name: "", role: "", type: "referee", location: "", phone: "", email: "", order: 0 }),
  },
  news: {
    endpoint: "/news",
    fields: [
      { name: "title", label: "Názov", type: "text", required: true },
      { name: "category", label: "Kategória", type: "text" },
      { name: "date", label: "Dátum", type: "date", required: true },
      { name: "excerpt", label: "Krátky popis", type: "textarea", required: true },
      { name: "content", label: "Obsah", type: "longtext" },
      { name: "link", label: "Odkaz", type: "text" },
      { name: "attachment_url", label: "Príloha (PDF, obrázok, dokument)", type: "attachment" },
    ],
    columns: [
      { key: "date", label: "Dátum", render: (v) => formatDate(v) },
      { key: "category", label: "Kat." },
      { key: "title", label: "Názov" },
    ],
    defaults: () => ({ title: "", category: "Aktuality", date: today(), excerpt: "", content: "", link: "", attachment_url: "", attachment_name: "" }),
  },
  projects: {
    endpoint: "/projects",
    fields: [
      { name: "title", label: "Názov projektu", type: "text", required: true },
      { name: "year", label: "Rok", type: "text" },
      { name: "description", label: "Popis", type: "textarea", required: true },
      { name: "link", label: "Odkaz", type: "text" },
      { name: "attachment_url", label: "Príloha (PDF, obrázok, dokument)", type: "attachment" },
    ],
    columns: [
      { key: "title", label: "Názov" },
      { key: "year", label: "Rok" },
    ],
    defaults: () => ({ title: "", year: String(new Date().getFullYear()), description: "", link: "", attachment_url: "", attachment_name: "" }),
  },
  structure: {
    endpoint: "/structure",
    fields: [
      { name: "name", label: "Meno", type: "text", required: true },
      { name: "role", label: "Funkcia", type: "text", required: true },
      { name: "commission", label: "Komisia", type: "select", options: ["VV", "Sekretariát", "ŠTK", "DK", "KR", "RK", "TMK", "KM", "OdK", "VK"], required: true },
      { name: "location", label: "Mesto / Klub", type: "text" },
      { name: "order", label: "Poradie", type: "number" },
    ],
    columns: [
      { key: "name", label: "Meno" },
      { key: "commission", label: "Komisia" },
      { key: "role", label: "Funkcia" },
    ],
    defaults: () => ({ name: "", role: "", commission: "VV", location: "", order: 0 }),
  },
  info_pages: {
    endpoint: "/info-pages",
    fields: [
      { name: "slug", label: "Slug (URL kľúč, napr. kurz-trenerov)", type: "text", required: true },
      { name: "title", label: "Názov stránky", type: "text", required: true },
      { name: "subtitle", label: "Podtitul", type: "text" },
      { name: "content", label: "Obsah (markdown — **tučný**, • zoznam, URL automaticky)", type: "longtext" },
      { name: "external_link", label: "Externý odkaz (voliteľné)", type: "text" },
      { name: "in_menu", label: "Zobraziť v menu 'Viac'", type: "checkbox" },
      { name: "order", label: "Poradie v menu", type: "number" },
      { name: "attachment_url", label: "Príloha (PDF, obrázok, dokument)", type: "attachment" },
    ],
    columns: [
      { key: "order", label: "#" },
      { key: "title", label: "Názov" },
      { key: "slug", label: "Slug" },
      { key: "in_menu", label: "V menu", render: (v) => (v ? "Áno" : "—") },
    ],
    defaults: () => ({ slug: "", title: "", subtitle: "", content: "", external_link: "", in_menu: true, order: 99, attachment_url: "", attachment_name: "" }),
  },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("announcements");

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (!user || !["admin", "referee"].includes(user.role)) {
    return <div className="wrap py-16 text-ink-500">Načítavam...</div>;
  }

  const visibleTabs = TABS.filter((t) => t.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-bg" data-testid="admin-dashboard">
      <Seo title="Administrácia" noindex />
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="wrap py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/" className="w-10 h-10 rounded-md bg-ink-900 text-white flex items-center justify-center font-heading font-bold">
              ObFZ
            </Link>
            <div className="leading-tight">
              <div className="font-heading font-bold text-ink-900 text-sm">Administrácia</div>
              <div className="text-xs text-ink-500">{user.username}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn btn-ghost" data-testid="admin-view-site">Pozrieť stránku</Link>
            <button type="button" onClick={handleLogout} className="btn btn-dark" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Odhlásiť
            </button>
          </div>
        </div>
      </header>

      <div className="wrap py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="space-y-1">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm font-semibold transition-colors ${
                    tab === t.key ? "bg-ink-900 text-white" : "text-ink-900 hover:bg-ink-50"
                  }`}
                  data-testid={`admin-tab-${t.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="lg:col-span-9">
          {tab === "contact" ? (
            <ContactEditor />
          ) : tab === "settings" ? (
            <SettingsEditor />
          ) : (
            <CrudPanel schemaKey={tab} schema={SCHEMAS[tab]} role={user.role} />
          )}
        </main>
      </div>
    </div>
  );
}

// ---------- Generic CRUD ----------
function CrudPanel({ schemaKey, schema: baseSchema, role }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const schema = useMemo(() => {
    if (role === "referee" && schemaKey === "announcements") {
      return {
        ...baseSchema,
        fields: baseSchema.fields.map((f) =>
          f.name === "category" ? { ...f, options: ["KR", "Obsadenie R"] } : f
        ),
        defaults: () => ({ ...baseSchema.defaults(), category: "KR" }),
      };
    }
    return baseSchema;
  }, [baseSchema, schemaKey, role]);

  const refresh = () => {
    setLoading(true);
    api.get(schema.endpoint)
      .then((r) => {
        let data = r.data;
        if (role === "referee" && schemaKey === "announcements") {
          data = data.filter((a) => ["KR", "Obsadenie R"].includes(a.category));
        }
        setItems(data);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    setEditing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaKey]);

  const handleNew = () => {
    setError("");
    setEditing({ __new: true, ...schema.defaults() });
  };

  const handleEdit = (item) => {
    setError("");
    setEditing({ ...item });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Naozaj chcete vymazať túto položku?")) return;
    try {
      await api.delete(`${schema.endpoint}/${id}`);
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const data = { ...editing };
    delete data.__new;
    // ensure number fields are numbers
    schema.fields.forEach((f) => {
      if (f.type === "number") data[f.name] = Number(data[f.name] || 0);
      if (f.type === "checkbox") data[f.name] = !!data[f.name];
    });
    try {
      if (editing.__new || !editing.id) {
        await api.post(schema.endpoint, data);
      } else {
        await api.put(`${schema.endpoint}/${editing.id}`, data);
      }
      setEditing(null);
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`crud-${schemaKey}`}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-ink-900">
          {TABS.find((t) => t.key === schemaKey)?.label}
        </h2>
        <button type="button" onClick={handleNew} className="btn btn-primary" data-testid="crud-new-btn">
          <Plus className="w-4 h-4" /> Pridať novú položku
        </button>
      </div>

      {error && (
        <div className="bg-red-bg border border-red text-red text-sm px-3 py-2 rounded mb-4">{error}</div>
      )}

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              {schema.columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              <th className="w-32 text-right">Akcie</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={schema.columns.length + 1} className="text-center text-ink-500 py-6">Načítavam...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={schema.columns.length + 1} className="text-center text-ink-500 py-6">Žiadne položky.</td></tr>
            )}
            {items.map((item) => (
              <tr key={item.id} data-testid={`crud-row-${item.id}`}>
                {schema.columns.map((c) => (
                  <td key={c.key} className="align-top">
                    {c.render ? c.render(item[c.key]) : String(item[c.key] ?? "")}
                  </td>
                ))}
                <td className="text-right">
                  <button type="button" onClick={() => handleEdit(item)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-ink-50 text-ink-900"
                          data-testid={`edit-${item.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => handleDelete(item.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-red-bg text-red ml-1"
                          data-testid={`delete-${item.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)}>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="crud-form">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-ink-900">
                {editing.__new ? "Pridať položku" : "Upraviť položku"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="p-1 rounded hover:bg-ink-50">
                <X className="w-4 h-4" />
              </button>
            </div>

            {schema.fields.map((f) => (
              f.type === "attachment" ? (
                <AttachmentField
                  key={f.name}
                  field={f}
                  url={editing.attachment_url}
                  name={editing.attachment_name}
                  onChange={({ url, name }) =>
                    setEditing((prev) => ({ ...prev, attachment_url: url, attachment_name: name }))
                  }
                />
              ) : (
                <FieldRenderer
                  key={f.name}
                  field={f}
                  value={editing[f.name]}
                  onChange={(v) => setEditing({ ...editing, [f.name]: v })}
                />
              )
            ))}

            {error && (
              <div className="bg-red-bg border border-red text-red text-sm px-3 py-2 rounded">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="btn btn-ghost">Zrušiť</button>
              <button type="submit" className="btn btn-primary" data-testid="crud-save-btn">
                <Save className="w-4 h-4" /> Uložiť
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <div>
        <label className="label">{field.label}{field.required && " *"}</label>
        <textarea
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="input resize-y"
          data-testid={`field-${field.name}`}
        />
      </div>
    );
  }
  if (field.type === "longtext") {
    return (
      <div>
        <label className="label">{field.label}{field.required && " *"}</label>
        <textarea
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={10}
          className="input resize-y font-mono text-sm"
          data-testid={`field-${field.name}`}
        />
        <p className="text-xs text-ink-500 mt-1">Tip: Markdown-style **tučný text** sa zobrazí ako tučný v detaile.</p>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <label className="label">{field.label}{field.required && " *"}</label>
        <select
          required={field.required}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="input"
          data-testid={`field-${field.name}`}
        >
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm font-medium text-ink-900 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
               data-testid={`field-${field.name}`} />
        {field.label}
      </label>
    );
  }
  return (
    <div>
      <label className="label">{field.label}{field.required && " *"}</label>
      <input
        type={field.type}
        required={field.required}
        value={value ?? ""}
        onChange={(e) => onChange(field.type === "number" ? e.target.value : e.target.value)}
        className="input"
        data-testid={`field-${field.name}`}
      />
    </div>
  );
}

function AttachmentField({ field, url, name, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ url: data.url, name: data.name });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Nahrávanie zlyhalo.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label className="label">{field.label}</label>
      {url ? (
        <div className="flex items-center justify-between gap-3 border border-border rounded px-3 py-2 bg-ink-50" data-testid="attachment-current">
          <span className="flex items-center gap-2 text-sm text-ink-900 truncate">
            <Paperclip className="w-4 h-4 text-green-700 flex-shrink-0" />
            <span className="truncate">{name || "Príloha"}</span>
          </span>
          <button type="button" onClick={() => onChange({ url: "", name: "" })}
                  className="text-red hover:bg-red-bg rounded p-1 flex-shrink-0" data-testid="attachment-remove-btn"
                  aria-label="Odstrániť prílohu">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-2 border border-dashed border-border rounded px-3 py-3 cursor-pointer hover:bg-ink-50 text-sm text-ink-700"
               data-testid="attachment-upload-label">
          <Upload className="w-4 h-4 text-ink-500" />
          {uploading ? "Nahrávam..." : "Nahrať súbor (PDF, obrázok, DOC/XLS… max 20 MB)"}
          <input type="file" className="hidden" onChange={handleFile} disabled={uploading}
                 accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.odt,.xls,.xlsx"
                 data-testid="attachment-file-input" />
        </label>
      )}
      {err && <p className="text-xs text-red mt-1" data-testid="attachment-error">{err}</p>}
    </div>
  );
}

function Modal({ children, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-md border border-border w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ---------- Contact editor ----------
function ContactEditor() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/contact-info").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    try {
      await api.put("/contact-info", data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="text-ink-500">Načítavam...</div>;

  const F = ({ name, label, type = "text" }) => (
    <div>
      <label className="label">{label}</label>
      <input type={type} value={data[name] ?? ""} onChange={(e) => setData({ ...data, [name]: e.target.value })}
             className="input" data-testid={`contact-${name}`} />
    </div>
  );

  return (
    <div data-testid="contact-editor">
      <h2 className="text-2xl font-bold text-ink-900 mb-5">Kontaktné údaje</h2>
      <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-border rounded-md p-6">
        <F name="organization" label="Názov organizácie" />
        <F name="address" label="Adresa" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F name="secretary_name" label="Meno tajomníčky / tajomníka" />
          <F name="secretary_phone" label="Mobil tajomníka" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F name="email" label="Hlavný e-mail" type="email" />
          <F name="secretary_email" label="E-mail sekretariátu" type="email" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F name="referee_email" label="E-mail KR" type="email" />
          <F name="phone" label="Telefón" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F name="secondary_phone" label="Druhý telefón" />
          <F name="office_hours" label="Úradné hodiny" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F name="facebook_url" label="Facebook URL" />
          <F name="futbalnet_url" label="Futbalnet URL" />
        </div>

        <div className="border-t border-ink-100 pt-4 mt-2">
          <div className="mono text-[11px] uppercase tracking-widest text-green-700 mb-3">Fakturačné údaje</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F name="ico" label="IČO" />
            <F name="dic" label="DIČ" />
            <F name="bank" label="Banka" />
            <F name="bank_account" label="Číslo účtu" />
          </div>
          <div className="mt-4">
            <F name="iban" label="IBAN" />
          </div>
          <div className="mt-4">
            <label className="label">Poznámka k platbám</label>
            <textarea value={data.payment_note ?? ""} onChange={(e) => setData({ ...data, payment_note: e.target.value })}
                      rows={2} className="input resize-y" data-testid="contact-payment_note" />
          </div>
        </div>

        {error && <div className="bg-red-bg border border-red text-red text-sm px-3 py-2 rounded">{error}</div>}
        {saved && <div className="bg-ink-900/10 border border-green-700 text-green-700 text-sm px-3 py-2 rounded">Uložené.</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" data-testid="contact-save-btn">
            <Save className="w-4 h-4" /> Uložiť
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Site Settings editor ----------
function SettingsEditor() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    try {
      await api.put("/settings", data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="text-ink-500">Načítavam...</div>;

  return (
    <div data-testid="settings-editor">
      <h2 className="text-2xl font-bold text-ink-900 mb-5">Nastavenia stránky</h2>
      <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-border rounded-md p-6">
        <div>
          <label className="label">Aktuálna sezóna</label>
          <input type="text" value={data.current_season ?? ""} onChange={(e) => setData({ ...data, current_season: e.target.value })}
                 className="input" data-testid="settings-season" />
        </div>
        <div>
          <label className="label">Posledná aktualizácia (zobrazené v info paneli)</label>
          <input type="text" value={data.last_update ?? ""} onChange={(e) => setData({ ...data, last_update: e.target.value })}
                 className="input" placeholder="napr. 02.05.2026" data-testid="settings-update" />
        </div>
        <div>
          <label className="label">Podtitul hero sekcie</label>
          <textarea value={data.hero_subtitle ?? ""} onChange={(e) => setData({ ...data, hero_subtitle: e.target.value })}
                    rows={3} className="input resize-y" data-testid="settings-subtitle" />
        </div>

        {error && <div className="bg-red-bg border border-red text-red text-sm px-3 py-2 rounded">{error}</div>}
        {saved && <div className="bg-ink-900/10 border border-green-700 text-green-700 text-sm px-3 py-2 rounded">Uložené.</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" data-testid="settings-save-btn">
            <Save className="w-4 h-4" /> Uložiť
          </button>
        </div>
      </form>
    </div>
  );
}
